import FieldWrapper from '@features/ui-form/components/FieldWrapper';
import Autocomplete from '@features/ui-form/components/Autocomplete';
import { WrappedSelectProps } from '@features/ui-form/types';
import { OBJECT_CATEGORIES, ObjectCategory } from '@root/src/entities/SpaceObject/category/enum';
import { useTranslation } from 'next-i18next';
import type { ChangeEvent } from 'react';

const CategoryAutocomplete = Autocomplete(false, false);

export const CategoriesFilterInput = ({ value, onChange, hideError = false, ...rest }: WrappedSelectProps) => {
  const { t } = useTranslation('common');
  const CategoriesAutocomplete = Autocomplete(true, false);

  const handleChange = (event: { target: { value: string } }) => {
    if (!onChange) return;

    const raw = event.target.value;
    let parsed: string[] = [];

    if (raw) {
      try {
        const tmp = JSON.parse(raw) as string[];
        if (Array.isArray(tmp)) parsed = tmp;
      } catch {
        parsed = [];
      }
    }

    onChange({
      target: {
        name: (rest as { name?: string }).name ?? 'category',
        value: parsed,
      },
    } as unknown as ChangeEvent<HTMLSelectElement>);
  };

  return (
    <FieldWrapper name="category" label={t('spaceObject.category')} hideError={hideError}>
      {CategoriesAutocomplete({
        value: Array.isArray(value) ? JSON.stringify(value) : '[]',
        onChange: handleChange,
        options: Object.values(OBJECT_CATEGORIES).map((category: ObjectCategory) => ({
          value: category,
          label: t(`spaceObject.category.${category}`),
          labelString: t(`spaceObject.category.${category}`),
        })),
        placeholder: t('spaceObject.category.placeholder'),
      })}
    </FieldWrapper>
  );
};

export interface CategoryInputProps {
  /** An object category, `''` for none. */
  value: string;
  onChange: (category: string) => void;
  error?: boolean;
  placeholder?: string;
}

/**
 * Picks one object category among the enum. Holds the plain value:
 * `disableSerialization` makes the Autocomplete speak it as is, `''` on clear.
 * Wrap it in a `FormField` (or a `FieldWrapper`) for its label and error.
 */
export const CategoryInput = ({ value, onChange, error, placeholder }: CategoryInputProps) => {
  const { t } = useTranslation('common');

  return (
    <CategoryAutocomplete
      value={value}
      onChange={event => onChange(event.target.value)}
      options={Object.values(OBJECT_CATEGORIES).map((category: ObjectCategory) => ({
        value: category,
        label: t(`spaceObject.category.${category}`),
        labelString: t(`spaceObject.category.${category}`),
      }))}
      placeholder={placeholder ?? t('spaceObject.category.placeholder')}
      error={error}
      disableSerialization
    />
  );
};
